import React from 'react';
import { useParams } from 'react-router-dom';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { IconArrowRight, type TablerIcon } from '@tabler/icons-react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Flex, SemiTitle } from '~/design-system';
import {
  getDescriptors,
  type ImporterDescriptor,
} from '../../../descriptors';

const iconTile = { bg: '#dbeafe', fg: '#2563eb' };

type ImportTypeSelectorProps = {
  includeLegacy?: boolean;
};

export default function ImportTypeSelector({
  includeLegacy = false,
}: ImportTypeSelectorProps) {
  const all = getDescriptors();
  const visible = includeLegacy
    ? all
    : all.filter((d) => d.status === 'active');

  const productData = visible.filter((d) => d.category === 'product-data');
  const masterList = visible.filter((d) => d.category === 'master-list');
  const other = visible.filter((d) => d.category === 'other');

  const { workspaceId } = useParams<{ workspaceId: string }>();

  return (
    <React.Fragment>
      {productData.length > 0 && (
        <>
          <SectionHeading>
            <Trans>Product Data</Trans>
          </SectionHeading>
          <Grid items={productData} workspaceId={workspaceId} />
        </>
      )}
      {masterList.length > 0 && (
        <>
          <SectionHeading>
            <Trans>Master Lists</Trans>
          </SectionHeading>
          <Grid items={masterList} workspaceId={workspaceId} />
        </>
      )}
      {other.length > 0 && (
        <>
          <SectionHeading>
            <Trans>Other imports</Trans>
          </SectionHeading>
          <Grid items={other} workspaceId={workspaceId} />
        </>
      )}
    </React.Fragment>
  );
}

function Grid({
  items,
  workspaceId,
}: {
  items: ImporterDescriptor[];
  workspaceId: string;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: [
          'minmax(0, 1fr)',
          'repeat(2, minmax(0, 1fr))',
          'repeat(3, minmax(0, 1fr))',
        ],
        gap: 3,
        width: '100%',
        marginBottom: 4,
      }}
    >
      {items.map((descriptor) => (
        <ImportCard
          key={descriptor.id}
          descriptor={descriptor}
          to={`/${workspaceId}/${descriptor.id}`}
        />
      ))}
    </Box>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ marginTop: 4, marginBottom: 3 }}>
      <SemiTitle>{children}</SemiTitle>
    </Box>
  );
}

function ImportCard({
  to,
  descriptor,
}: {
  to: string;
  descriptor: ImporterDescriptor;
}) {
  const { i18n } = useLingui();
  const Icon = descriptor.icon as TablerIcon;
  const isLegacy = descriptor.status === 'legacy';
  return (
    <Box
      as={RouterLink}
      // @ts-ignore - react-router-dom 'to' prop isn't on Box typing
      to={to}
      sx={{
        display: 'block',
        textAlign: 'left',
        padding: 3,
        borderRadius: 8,
        border: '1px solid #e5e7eb',
        backgroundColor: 'white',
        opacity: isLegacy ? 0.75 : 1,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        transition:
          'box-shadow 120ms ease, border-color 120ms ease, transform 120ms ease',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        '&:hover': {
          borderColor: '#c7d2fe',
          textDecoration: 'none',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
        },
        '&:focus': {
          outline: 'none',
          borderColor: '#2563eb',
        },
      }}
    >
      <Flex
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 3,
        }}
      >
        <Flex
          sx={{
            width: 40,
            height: 40,
            borderRadius: 6,
            backgroundColor: iconTile.bg,
            color: iconTile.fg,
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={22} stroke={1.75} />
        </Flex>
        <Box sx={{ color: '#9ca3af' }}>
          <IconArrowRight size={18} stroke={1.75} />
        </Box>
      </Flex>
      <Box
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          color: '#111827',
          marginBottom: 1,
          lineHeight: 1.3,
        }}
      >
        {i18n._(descriptor.title)}
      </Box>
      <Box
        sx={{
          fontSize: '14px',
          fontWeight: 400,
          color: '#6b7280',
          lineHeight: 1.45,
        }}
      >
        {i18n._(descriptor.whenToUse)}
      </Box>
    </Box>
  );
}
